import { ensureXxhash, xxhash64 } from './xxhash';
import type { TTSProfile } from './zeroTTSProfile';

/** Fixed ZeroTTS namespace seed. Changing this invalidates every installed
 *  atom cache, so treat it as an append-only constant across versions. */
const ZT_SEED = 0xc0defaceCafebabEn;

export async function ensureZeroTTSBytes(): Promise<void> {
  await ensureXxhash();
}

export function ztHash(...coords: (string | number)[]): bigint {
  const payload = coords.map(String).join('|');
  return xxhash64(new TextEncoder().encode(payload), ZT_SEED);
}

export function ztHashHex(...coords: (string | number)[]): string {
  return ztHash(...coords).toString(16).padStart(16, '0');
}

export function ztPick<T>(items: T[], ...coords: (string | number)[]): T {
  if (items.length === 0) throw new Error('[zerotts] ztPick called on empty array');
  const h = ztHash(...coords);
  const idx = Number(h % BigInt(items.length));
  return items[idx];
}

export interface EnumeratedAtom {
  atom_text: string;
  context_name: string;
  tier: 1 | 2 | 3;
}

export function extractSlots(template: string): string[] {
  const re = /\{(\w+)\}/g;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(template)) !== null) out.push(m[1]);
  return out;
}

export function fillTemplate(
  template: string,
  slots: string[],
  values: string[],
): string {
  let result = template;
  slots.forEach((slot, i) => {
    result = result.replace(`{${slot}}`, values[i]);
  });
  return result;
}

export function* cartesianProduct<T>(arrays: T[][]): Generator<T[]> {
  if (arrays.length === 0) {
    yield [];
    return;
  }
  const [first, ...rest] = arrays;
  for (const item of first) {
    for (const combo of cartesianProduct(rest)) {
      yield [item, ...combo];
    }
  }
}

export function enumerateAtoms(profile: TTSProfile): EnumeratedAtom[] {
  const atoms = new Map<string, EnumeratedAtom>();
  const cap = profile.install.max_atoms ?? 100;

  for (const [ctxName, ctx] of Object.entries(profile.contexts)) {
    for (const template of ctx.templates) {
      const slots = extractSlots(template);
      if (slots.length === 0) {
        if (!atoms.has(template)) {
          atoms.set(template, {
            atom_text: template,
            context_name: ctxName,
            tier: ctx.tier,
          });
          if (atoms.size >= cap) return Array.from(atoms.values());
        }
        continue;
      }
      const slotValues = slots.map((s) => profile.pools[s] ?? []);
      if (slotValues.some((v) => v.length === 0)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[zerotts] template "${template}" references missing pool, skipped`,
        );
        continue;
      }
      for (const combo of cartesianProduct(slotValues)) {
        const atomText = fillTemplate(template, slots, combo);
        if (!atoms.has(atomText)) {
          atoms.set(atomText, {
            atom_text: atomText,
            context_name: ctxName,
            tier: ctx.tier,
          });
          if (atoms.size >= cap) return Array.from(atoms.values());
        }
      }
    }
  }
  return Array.from(atoms.values());
}

export interface ResolvedAtom {
  atomText: string;
  cacheKey: string;
  keyHashHex: string;
}

export function resolveAtomAtRuntime(
  profile: TTSProfile,
  contextName: string,
  callIndex: number,
  payload: Record<string, string>,
): ResolvedAtom {
  const ctx = profile.contexts[contextName];
  if (!ctx) throw new Error(`[zerotts] unknown context: ${contextName}`);
  if (ctx.templates.length === 0) {
    throw new Error(`[zerotts] context "${contextName}" has no templates`);
  }

  const template = ztPick(
    ctx.templates,
    profile.persona_seed,
    contextName,
    callIndex,
    0,
  );

  const slots = extractSlots(template);
  let atomText = template;
  slots.forEach((slot, slotIdx) => {
    const payloadValue = payload[slot];
    const pool = profile.pools[slot] ?? [slot];
    const value =
      payloadValue ??
      ztPick(
        pool,
        profile.persona_seed,
        contextName,
        callIndex,
        slotIdx + 1,
      );
    atomText = atomText.replace(`{${slot}}`, value);
  });

  const keyHashHex = ztHashHex(profile.persona_seed, atomText);
  return {
    atomText,
    cacheKey: `/zerotts-atom/${keyHashHex}`,
    keyHashHex,
  };
}
